import type { AiMoment } from '../core/ai-notes';
import { splitWhere } from '../core/group';
import { modeChanges } from '../core/moments';
import { momentTier, noteTier, reasonTier } from '../core/ranking';
import { clampTrigger, type PlacedClip, type Timeline } from '../core/timeline';
import { AUTOPILOT_LABELS, type Trace } from '../core/telemetry';
import { momentLabel } from '../core/format';
import type { Library, LibraryFile, LibraryFootage } from './library';
import type { Marker, MarkerKind, Span } from './markers';
import type { ScrubberModel, SpeedPoint } from './scrubber';

const MOMENT_LEAD_IN_MS = 5000;
const TRIGGER_LEAD_IN_MS = 10_000;
const CHANGE_LEAD_IN_MS = 2000;
const CHANGE_WINDOW_MS = 60_000;
const INCIDENT_TIER = 3;
const AUTOPILOT_OFF_TIER = 6;
const MODE_TIER = 9;
const MANUAL = 0;

export interface RankedMarker extends Marker {
  readonly tier: number;
}

export interface ViewerModel {
  readonly scrubber: ScrubberModel;
  readonly markers: readonly RankedMarker[];
  readonly trigger: number | undefined;
  readonly startTime: number;
}

type Placed = PlacedClip<LibraryFile>;

const fronts = (timeline: Timeline<LibraryFile>): readonly Placed[] =>
  timeline.slots.flatMap((slot) => slot.clips.filter((placed) => placed.clip.camera === 'front'));

const binTime = (placed: Placed, trace: Trace, bin: number): number => placed.start + (trace.start + bin * trace.step) * 1000;

function speedPoints(placed: Placed, trace: Trace): SpeedPoint[] {
  return Array.from(trace.speedMax).flatMap((max, bin) =>
    Number.isNaN(max) ? [] : [{ time: binTime(placed, trace, bin), min: trace.speedMin[bin]!, max }],
  );
}

function brakeSpans(placed: Placed, trace: Trace): Span[] {
  const bins = Array.from(trace.brake).flatMap((brake, bin) => (brake ? [bin] : []));
  return splitWhere(bins, (previous, bin) => bin - previous > 1).map((run) => ({
    start: binTime(placed, trace, run[0]!),
    end: binTime(placed, trace, run.at(-1)! + 1),
  }));
}

function momentMarkers(library: Library, placed: Placed): RankedMarker[] {
  return (library.scanOf(placed.clip)?.moments ?? []).flatMap((moment) =>
    moment.kind === 'disengage' ? [] : [{ kind: moment.kind, time: placed.start + moment.time * 1000, label: momentLabel(moment), tier: momentTier(moment) }],
  );
}

function modeMarkers(library: Library, fronts: readonly Placed[]): RankedMarker[] {
  const placed = fronts.flatMap((front) => {
    const telemetry = library.scanOf(front.clip)?.telemetry;
    return telemetry ? [{ start: front.start, telemetry }] : [];
  });
  return modeChanges(placed).map((change) => ({
    kind: 'mode',
    time: change.time,
    label: `${AUTOPILOT_LABELS[change.from] ?? 'Unknown'} → ${AUTOPILOT_LABELS[change.to] ?? 'Unknown'}`,
    tier: change.to === MANUAL ? AUTOPILOT_OFF_TIER : MODE_TIER,
  }));
}

const noteMarkerKind = (moment: AiMoment): MarkerKind => (noteTier(moment) <= INCIDENT_TIER ? 'incident' : moment.kind === 'change' ? 'change' : 'nearby');

function noteMarkers(library: Library, footage: LibraryFootage, timeline: Timeline<LibraryFile>): RankedMarker[] {
  return (library.notesOf(footage)?.moments ?? []).flatMap((moment) => {
    const time = moment.peak - timeline.utcOffset;
    return time < timeline.start || time > timeline.end ? [] : [{ kind: noteMarkerKind(moment), time, label: moment.label, tier: noteTier(moment) }];
  });
}

const lastChangeBefore = (moments: readonly RankedMarker[], anchor: number): RankedMarker | undefined =>
  moments.filter((moment) => moment.kind === 'change' && moment.time <= anchor && anchor - moment.time <= CHANGE_WINDOW_MS).sort((a, b) => b.time - a.time)[0];

function wantedStart(timeline: Timeline<LibraryFile>, moments: readonly RankedMarker[], trigger: number | undefined): number {
  const strongest = moments.filter((moment) => moment.tier < MODE_TIER).sort((a, b) => a.tier - b.tier || a.time - b.time)[0];
  const anchor = strongest?.time ?? trigger;
  const change = anchor === undefined ? undefined : lastChangeBefore(moments, anchor);
  if (change) return change.time - CHANGE_LEAD_IN_MS;
  if (strongest) return strongest.time - MOMENT_LEAD_IN_MS;
  return trigger === undefined ? timeline.start : trigger - TRIGGER_LEAD_IN_MS;
}

const startTime = (timeline: Timeline<LibraryFile>, moments: readonly RankedMarker[], trigger: number | undefined): number =>
  Math.min(timeline.end, Math.max(timeline.start, wantedStart(timeline, moments, trigger)));

export function buildViewerModel(library: Library, footage: LibraryFootage, timeline: Timeline<LibraryFile>, triggerTier: number): ViewerModel {
  const info = library.infoOf(footage);
  const trigger = clampTrigger(timeline, info?.timestamp);
  const traced = fronts(timeline).flatMap((placed) => {
    const trace = library.scanOf(placed.clip)?.trace;
    return trace ? [{ placed, trace }] : [];
  });
  const moments = [
    ...fronts(timeline).flatMap((placed) => momentMarkers(library, placed)),
    ...modeMarkers(library, fronts(timeline)),
    ...noteMarkers(library, footage, timeline),
  ];
  const incident = info !== undefined && reasonTier(info.reason) <= INCIDENT_TIER;
  const triggerMarker: RankedMarker[] = trigger === undefined ? [] : [{ kind: incident ? 'incident' : 'trigger', time: trigger, label: info?.reason.label ?? 'Trigger', tier: triggerTier }];
  const markers = [...triggerMarker, ...moments].sort((a, b) => a.time - b.time);
  return {
    scrubber: {
      span: { start: timeline.start, end: timeline.end },
      utcOffset: timeline.utcOffset,
      coverage: timeline.slots.map((slot) => ({ start: slot.start, end: slot.end })),
      speed: traced.flatMap(({ placed, trace }) => speedPoints(placed, trace)),
      brake: traced.flatMap(({ placed, trace }) => brakeSpans(placed, trace)),
      markers,
    },
    markers,
    trigger,
    startTime: startTime(timeline, moments, trigger),
  };
}
